import { Controller, Control, UseFormRegister, UseFormSetValue, UseFormTrigger, FieldErrors } from 'react-hook-form'
import { LabelInput } from '@/common/ui/LabelInput'
import { Label } from '@/common/ui'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/common/ui/select'
import { InputError } from '@/common/ui/InputError'
import { formatMobileNumber, MAX_LENGTH } from '@/onboarding/features/mobile/rules/mobileFormatting'
import { canValidate } from '@/onboarding/features/mobile/rules/mobileValidation'
import { ONBOARDING_COPY } from '@/locales/en'

interface MobileNumberFieldProps {
    control: Control<any>
    register: UseFormRegister<any>
    setValue: UseFormSetValue<any>
    trigger: UseFormTrigger<any>
    countryCode: string
    mobile: string
    mobileError?: string
    countryCodeError?: string
    formErrors?: FieldErrors<any>
}

/**
 * Reusable Mobile Number field component.
 *
 * Encapsulates the country code selector + mobile number input pattern used across onboarding forms.
 * Handles:
 * - Country code selection (+61 Australia, +64 New Zealand)
 * - Mobile number formatting and validation
 * - Error display for both fields
 *
 * Validation is handled by the schema (must use createMobileValidator or equivalent).
 */
export function MobileNumberField({
    control,
    register,
    setValue,
    trigger,
    countryCode,
    mobile,
    mobileError,
    countryCodeError,
    formErrors,
}: MobileNumberFieldProps) {
    return (
        <div className="flex flex-col w-full">
            <Label htmlFor="mobile">{ONBOARDING_COPY.aboutYou.form.mobile}</Label>
            <div className="flex">
                <Controller
                    name="countryCode"
                    control={control}
                    render={({ field }) => {
                        const onChange = (value: string) => {
                            field.onChange()
                            setValue('countryCode', value)
                            trigger('countryCode')
                        }
                        return (
                            <Select value={countryCode} onValueChange={onChange}>
                                <SelectTrigger
                                    id="countryCode"
                                    aria-label="Country code"
                                    className="min-w-[100px] w-[120px] rounded-l-full border-gray h-10 focus:ring-0"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent {...field}>
                                    <SelectGroup>
                                        <SelectItem value="+61">🇦🇺 +61</SelectItem>
                                        <SelectItem value="+64">🇳🇿 +64</SelectItem>
                                    </SelectGroup>
                                </SelectContent>
                            </Select>
                        )
                    }}
                />
                <LabelInput
                    {...register('mobile')}
                    className="w-full rounded-l-[0px] rounded-r-full border border-l-0 border-gray py-2 px-4 h-10"
                    type="text"
                    id="mobile"
                    required
                    formNoValidate
                    placeholder={
                        countryCode === '+61'
                            ? ONBOARDING_COPY.aboutYou.form.mobileAuPlaceholder
                            : ONBOARDING_COPY.aboutYou.form.mobileNzPlaceholder
                    }
                    inputMode="numeric"
                    maxLength={MAX_LENGTH + 1}
                    value={formatMobileNumber(mobile, countryCode)}
                    onChange={(e) => {
                        setValue('mobile', e.target.value, {
                            shouldValidate: canValidate(e.target.value.length, countryCode),
                        })
                    }}
                    onBlur={() => {
                        trigger('mobile')
                    }}
                />
            </div>
            <InputError message={mobileError || (formErrors?.mobile?.message as string)} />
            <InputError message={countryCodeError || (formErrors?.countryCode?.message as string)} />
        </div>
    )
}
